// Display rule (CLAUDE.md): store UTC, render America/Toronto in en-CA.

export function formatDateTime(iso) {
  return new Date(iso).toLocaleString('en-CA', {
    timeZone: 'America/Toronto',
    dateStyle: 'medium',
    timeStyle: 'short',
  })
}

export function formatDate(iso) {
  return new Date(iso).toLocaleDateString('en-CA', {
    timeZone: 'America/Toronto',
    dateStyle: 'long',
  })
}

// The invite deadline with its time spelled out, e.g.
// "August 4, 2026 at 11:59 p.m. (Toronto time)". Mirrors format_toronto_datetime
// in app/services/invites.py EXACTLY — the composer preview must render the
// same string the send does. Hour/meridiem are assembled by hand because
// locale formatting drifts across ICU versions (narrow spaces, "p.m." vs "PM")
// and any drift here would be a preview lie.
export function formatTorontoDateTime(iso) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Toronto',
    hour: 'numeric',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date(iso))
  const get = (type) => parts.find((part) => part.type === type).value
  const hour24 = Number(get('hour'))
  const hour = hour24 % 12 || 12
  const meridiem = hour24 < 12 ? 'a.m.' : 'p.m.'
  return `${formatDate(iso)} at ${hour}:${get('minute')} ${meridiem} (Toronto time)`
}

// The same instant with its weekday in front, for the call screen's footnote
// about a call already taken. The weekday is what makes "you already did this"
// land — a bare date has to be worked out.
export function formatTorontoWeekdayDateTime(iso) {
  const weekday = new Date(iso).toLocaleDateString('en-CA', {
    timeZone: 'America/Toronto',
    weekday: 'long',
  })
  return `${weekday}, ${formatTorontoDateTime(iso)}`
}

// --- Spaces -------------------------------------------------------------------

// Y-M-D of an instant in Toronto, or of a date-only string as written. A space's
// end date may arrive either way; a date-only value must never be pushed
// through `new Date()` alone — it would parse as UTC midnight, which is the
// evening BEFORE in Toronto, and the list would show the wrong day.
function torontoParts(value) {
  const dateOnly = typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)
  if (dateOnly) {
    const [year, month, day] = value.split('-').map(Number)
    return { year, month, day }
  }
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Toronto',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  }).formatToParts(new Date(value))
  const get = (type) => Number(parts.find((part) => part.type === type).value)
  return { year: get('year'), month: get('month'), day: get('day') }
}

const MONTHS_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

// "14 Oct 2026" — the spaces table's date shape. Assembled from Toronto parts
// rather than a locale style, because no en-CA style puts the day first.
export function formatShortDate(value, { year = true } = {}) {
  const parts = torontoParts(value)
  const dayMonth = `${parts.day} ${MONTHS_SHORT[parts.month - 1]}`
  return year ? `${dayMonth} ${parts.year}` : dayMonth
}

// The value a <input type="date"> wants for this end date.
export function dateInputValue(value) {
  if (!value) return ''
  const { year, month, day } = torontoParts(value)
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`
}

// Whole calendar days from Toronto's today to a date; negative once it has
// passed. Both ends are compared as UTC-midnight calendar days, so a DST
// change never yields 20.96 days.
export function daysUntil(value, now = new Date()) {
  const target = torontoParts(value)
  const today = torontoParts(now.toISOString())
  const utc = ({ year, month, day }) => Date.UTC(year, month - 1, day)
  return Math.round((utc(target) - utc(today)) / 86_400_000)
}

// "in 21 days" / "tomorrow" / "today" / "3 days ago", for the Ends column.
export function formatRelativeDays(value, now = new Date()) {
  const days = daysUntil(value, now)
  if (days === 0) return 'today'
  if (days === 1) return 'tomorrow'
  if (days === -1) return 'yesterday'
  return days > 0 ? `in ${days} days` : `${-days} days ago`
}

// Last activity: "Just now", "12 minutes ago", "2 hours ago", "Yesterday", then
// "9 Sep" once it is older than that (with the year once it is another year).
export function formatRelativeActivity(iso, now = new Date()) {
  const then = new Date(iso)
  const minutes = Math.round((now - then) / 60_000)
  if (minutes < 1) return 'Just now'
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`
  const days = -daysUntil(iso, now)
  if (days === 0) {
    const hours = Math.round(minutes / 60)
    return `${hours} hour${hours === 1 ? '' : 's'} ago`
  }
  if (days === 1) return 'Yesterday'
  const sameYear = torontoParts(iso).year === torontoParts(now.toISOString()).year
  return formatShortDate(iso, { year: !sameYear })
}
